import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useState, useEffect, forwardRef } from "react";
import { Editicon, Deleteicon, Viewicon } from "../../../../Components/IconComponents";
import { Filter as FilterIcon } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import Table from "../../../../Components/UiComponents/Table/Table";
import SelectComponent from "../../../../Components/UiComponents/SelectComponent/SelectComponent";
import { deleteData, deleteDataById, getData } from "../../../../Services/Services";
import {
  getMasterResourceListURL,
  serviceBaseURL,
  appserveraddr,
  dmscoreservicecontextname,
  deleteMasterResourceById,
  deleteMasterResourceImageByName,
  getThemeURL,
  resourceActivityMasterListData,
  resourceCatMasterListData,
} from "../../../../Services/EndPoints";
import { getExportedColumnsData } from "../../../../Components/UiComponents/utils";
import { setTableData } from "../../../../CentralStore/ExportLMSTable/dataSlice";
import Moment from "react-moment";
import {
  masterResourceTableData,
  masterResourcecolumnsForExport,
} from "../../../../JsonData/MasterResourceConfig";
import { Link } from "react-router-dom";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import Loader from "react-spinner-loader";
import { useTranslation } from "react-i18next";

const TODAY_STR = new Date().toISOString().split("T")[0];

const filterLabelStyle = {
  fontSize: "9.5px",
  fontWeight: 800,
  letterSpacing: ".3px",
  textTransform: "uppercase",
  color: "#98A2B3",
};

const filterControlStyle = {
  padding: "6px 10px",
  borderRadius: "8px",
  border: "1px solid #E4E7EC",
  fontSize: "12px",
  background: "#fff",
  color: "#1D2939",
  height: "34px",
  boxSizing: "border-box",
};

// Same fixed unit list used by the Create Master Resource form's Unit dropdown.
const UNIT_FILTER_OPTIONS = [
  "Nos", "Bags", "Box", "Kg", "Liter", "Meter", "Metric", "Pair",
  "Persons", "Sets", "Tablets", "Team", "Vials",
].map((u) => ({ id: u, value: u }));

const MasterResourceTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const qs = require("qs");
  const [masterResourceData, setMasterResourceData] = useState(
    masterResourceTableData
  );
  const [searchData, setSearchData] = useState([]);
  const { t, i18n } = useTranslation();
  const [activityOptions, setActivityOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [activityFilter, setActivityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  // Date range only takes effect once "Apply" is clicked — pending values are
  // what the inputs show, applied values are what actually filters the data.
  const [pendingDateFrom, setPendingDateFrom] = useState("");
  const [pendingDateTo, setPendingDateTo] = useState("");
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");
  useEffect(() => {
    getMasterResourceList();
    getFilterOptions();
  }, []);

  useEffect(() => {
    sendDataToCentralStore(searchData);
  }, [searchData]);

  async function getMasterResourceList() {
    setLoader(true);

    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    let masterResourceList = await getData(`${getMasterResourceListURL}/${state_id}`);
    let sortedList = masterResourceList.sort((a, b) => {
      return new Date(b.created_on) - new Date(a.created_on);
    });
    setLoader(false);

    setData(sortedList);
  }

  // Same Activity/Category config APIs used by the Create Master Resource
  // form, so these filter options always match what's selectable when
  // creating/editing a master resource.
  async function getFilterOptions() {
    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    const [activityList, categoryList] = await Promise.all([
      getData(`${resourceActivityMasterListData}/${state_id}`),
      getData(`${resourceCatMasterListData}/${state_id}`),
    ]);
    setActivityOptions(
      (Array.isArray(activityList) ? activityList : [])
        .map((a) => a.activity)
        .filter(Boolean)
        .map((a) => ({ id: a, value: a }))
    );
    setCategoryOptions(
      (Array.isArray(categoryList) ? categoryList : [])
        .map((c) => c.category)
        .filter(Boolean)
        .map((c) => ({ id: c, value: c }))
    );
  }

  const isDateRangeInvalid =
    !!pendingDateFrom && !!pendingDateTo && new Date(pendingDateFrom) > new Date(pendingDateTo);

  const handleApplyDateFilter = () => {
    if (isDateRangeInvalid) return;
    setAppliedDateFrom(pendingDateFrom);
    setAppliedDateTo(pendingDateTo);
  };

  const filteredData = React.useMemo(() => {
    return (data || []).filter((row) => {
      if (activityFilter && row.activity !== activityFilter) return false;
      if (categoryFilter && row.category !== categoryFilter) return false;
      if (unitFilter && row.unit !== unitFilter) return false;
      if (appliedDateFrom || appliedDateTo) {
        if (!row.created_on) return false;
        const created = new Date(row.created_on);
        if (appliedDateFrom && created < new Date(`${appliedDateFrom}T00:00:00`)) return false;
        if (appliedDateTo && created > new Date(`${appliedDateTo}T23:59:59`)) return false;
      }
      return true;
    });
  }, [data, activityFilter, categoryFilter, unitFilter, appliedDateFrom, appliedDateTo]);

  const hasActiveFilters =
    activityFilter !== "" || categoryFilter !== "" || unitFilter !== "" || appliedDateFrom !== "" || appliedDateTo !== "";

  const clearAllFilters = () => {
    setActivityFilter("");
    setCategoryFilter("");
    setUnitFilter("");
    setPendingDateFrom("");
    setPendingDateTo("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
  };

  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = masterResourcecolumnsForExport;
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, data),
        })
      );
    }
  };
  const addNewHandler = () => {
    navigate("/master-resource/create-masterresource");
  };

  const getImageWithAuth = async (imageName) => {
    const imageUrl = `${appserveraddr}/${dmscoreservicecontextname}/master_resource/get_resource_image/${imageName}`;
  
    try {
      const response = await fetch(imageUrl, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem("auth_token"),
        },
      });
  
      if (response.ok) {
        const blob = await response.blob(); // Convert the response to a Blob
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
          reader.onloadend = () => {
            // Return the Base64-encoded string (exclude the data URL prefix part)
            resolve(reader.result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob); // Read the Blob as a Data URL (Base64)
        });
      } else {
        console.error("Failed to fetch image:", response.statusText);
        return null;
      }
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  };
  
  

  const columns = React.useMemo(
    () => [
      {
        Header: t("masterresourcetableheaders.activity"),
        accessor: "activity",
        Cell: (props) => {
          if (props.value) {
            let activityText = props.value;
            if (props.value.length > 15) {
              activityText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{activityText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("masterresourcetableheaders.category"),
        accessor: "category",
        Cell: (props) => {
          if (props.value) {
            let categoryText = props.value;
            if (props.value.length > 15) {
              categoryText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{categoryText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("masterresourcetableheaders.name"),
        accessor: "name",
        Cell: (props) => {
          if (props.value) {
            let name = props.value;
            if (props.value.length > 15) {
              name = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{name}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },

      {
        Header: t("masterresourcetableheaders.createdon"),
        accessor: "created_on",
        Cell: (props) => {
          if (props.value) {
            return <Moment format="DD/MM/YYYY, hh:mm A">{props.value}</Moment>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
        leftAlign: true,
      },
      {
        Header: t("masterresourcetableheaders.updatedon"),
        accessor: "updated_on",
        Cell: (props) => {
          if (props.value) {
            return <Moment format="DD/MM/YYYY, hh:mm A">{props.value}</Moment>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
        leftAlign: true,
      },
      {
        Header: t("masterresourcetableheaders.unit"),
        accessor: "unit",
        disableSortBy: true,
      },
      {
        Header: t("masterresourcetableheaders.image"),
        accessor: "resource_image_name",
        Cell: (props) => {
          const [image, setImage] = useState(null);
          const [loading, setLoading] = useState(true); // Track loading state
          const [hasImage, setHasImage] = useState(false); // Track if image exists
      
          useEffect(() => {
            if (props.value) {
              setImage(null); // Reset image on new value
              setLoading(true); // Set loading to true when a new image is being fetched
              setHasImage(false); // Reset the state indicating image existence
      
              // Fetch image and convert to Base64
              getImageWithAuth(props.value)
                .then((base64) => {
                  if (base64) {
                    setImage(base64); // Set the Base64 image data
                    setHasImage(true); // Mark that the image exists
                  } else {
                    setImage(null); // If fetch fails, clear image
                  }
                  setLoading(false); // Stop loading
                })
                .catch(() => {
                  setImage(null); // Clear image in case of error
                  setHasImage(false); // No image found
                  setLoading(false); // Stop loading in case of error
                });
            } else {
              setImage(null); // If no value for image, reset image
              setHasImage(false); // No image available
              setLoading(false); // Stop loading
            }
          }, [props.value]); // Re-run when image name changes
      
          // If there's no image and it's not loading, show "NA"
          if (!loading && !hasImage) {
            return <span>NA</span>;
          }
      
          // If loading, show "Loading..."
          if (loading) {
            return <span>Loading...</span>;
          }
      
          // Display the Base64 image
          if (image) {
            return <img className="rsourceImage" src={`data:image/jpeg;base64,${image}`}/>;
          } else {
            return <span>NA</span>; // Fallback message if image is not available
          }
        },
      },      
      
      
      {
        Header: t("masterresourcetableheaders.action"),
        disableSortBy: true,

        Cell: (props) => {
          const [isdelete, setIsdelete] = useState(false);
          const [selectedId, setSelectedId] = useState(null);
          const [selectedName, setSelectedName] = useState(null);
          const deletePopUp = (id,resource_image_name) => {
            setSelectedId(id);
            setSelectedName(resource_image_name);
            setIsdelete(!isdelete);
          };
          const deleteTableDataById = async () => {
            try {
              const response = await deleteDataById(deleteMasterResourceById, selectedId);
              if (response?.status === "success") {
                await getMasterResourceList();;
                toast.success(
                  response.msg || t("delete_success_message"),
                  {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                  }
                );
              } 
            } catch (error) {
              toast.error(t("delete_error_message"), {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
              });
              console.error("Delete error:", error);
            } finally {
              setIsdelete(false);
              setSelectedId(null);
            }
          };
          const deleteTableDataImageByName = async () => {
            try {
              const response = await deleteDataById(deleteMasterResourceImageByName, selectedName);
              if (response?.status === "success") {
                await getMasterResourceList();;
                toast.success(
                  response.msg || t("delete_success_message"),
                  {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                  }
                );
              }
            } catch (error) {
              toast.error(t("delete_error_message"), {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
              });
              console.error("Delete error:", error);
            } finally {
              setIsdelete(false);
              setSelectedName(null);
            }
          };

          const handleDelete = () => {
            deleteTableDataById();
            deleteTableDataImageByName();
          };
          // const deleteTableDataById = (id) => {
          //   deleteData(deleteMasterResourceById, id)
          //     .then((response) => {
          //       deletePopUp();
          //       getMasterResourceList();
          //     })
          //     .catch((error) => {
          //       console.log(error);
          //     });
          // };

          return (
            <div className="usertable__actionsicon">
              <span
                onClick={() =>
                  navigate("/master-resource/view-masterresource", {
                    state: { data: props.row.original },
                  })
                }
              >
                <Viewicon value={props.value} className="cursor-pointer me-2" />
              </span>

              <span
  onClick={() => {
    console.log("imgName:", props.row.original.resource_image_name); // Log the image name
    navigate("/master-resource/create-masterresource", {
      state: {
        id: props.row.original.id,
        imgName: props.row.original.resource_image_name, // Pass the image name in the state
        edit: true
      },
    });
  }}
>
  <Editicon value={props.value} className="cursor-pointer me-2" />
</span>


              <span onClick={() => deletePopUp(props.row.original.id,props.row.original.resource_image_name)}>
                <Deleteicon
                  value={props.value}
                  className="cursor-pointer mr-1"
                />
              </span>

              {isdelete && (
                <Confirmpopup
                  show={isdelete}
                  title={t("deletepopuptext")}
                  message={t("deletepopupmessage")}
                  handleToggle={deletePopUp}
                  row={props.row}
                  name={props.row.original.activity}
                  deleteRow={handleDelete}
                  cancel={t("cancelbutton")}
                  action={t("actionbutton")}
                />
              )}
            </div>
          );
        },
      },
    ],
    [t]
  );
  return (
    <div className="create-user masterresource-mgmt-table">
      <style>{`
        #light .masterresource-mgmt-table .iccc--table th {
          background: linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%) !important;
          color: #ffffff !important;
        }
        #light .masterresource-mgmt-table .iccc--table tbody tr td,
        #light .masterresource-mgmt-table .iccc--table tbody tr th {
          border: none !important;
        }
        #light .masterresource-mgmt-table .iccc--table tbody tr td {
          background-color: #fff !important;
        }
        #light .masterresource-mgmt-table .iccc--table tbody tr:nth-child(even) td {
          background-color: #F9FAFB !important;
        }
        .masterresource-mgmt-table .search-form {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
        .masterresource-mgmt-table .search-form input[type="text"] {
          height: 36px !important;
          line-height: 34px !important;
          padding: 0 40px 0 12px !important;
          border: 1px solid #E4E7EC !important;
          border-radius: 8px !important;
          font-size: 12px !important;
          box-sizing: border-box !important;
        }
        .masterresource-mgmt-table .search-form button {
          position: absolute !important;
          top: 0 !important;
          bottom: 0 !important;
          right: 0 !important;
          height: 36px !important;
          width: 36px !important;
          margin: 0 !important;
          padding: 0 !important;
          background-color: transparent !important;
          border: none !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .masterresource-mgmt-table .search-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .masterresource-mgmt-table .search-icon svg {
          display: block;
          color: #2E6DA4;
        }
      `}</style>
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      <div style={{ background: "#fff", border: "1px solid #EEF1F5", borderRadius: "10px", boxShadow: "0 1px 2px rgba(16,24,40,0.04)", padding: "10px 14px", margin: "12px 12px 12px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <span style={{ width: "20px", height: "20px", borderRadius: "6px", background: "#EBF4FA", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <FilterIcon size={11} color="#2E6DA4" />
          </span>
          <span style={{ fontSize: "12px", fontWeight: 800, color: "#2E6DA4" }}>Filters</span>
        </div>
        <style>{`
          .masterresource-filter-bar input[type="date"] {
            position: relative;
            padding-right: 30px;
            display: inline-flex;
            align-items: center;
          }
          .masterresource-filter-bar input[type="date"]::-webkit-datetime-edit {
            display: inline-flex;
            align-items: center;
            padding: 0;
            line-height: normal;
          }
          .masterresource-filter-bar input[type="date"]::-webkit-datetime-edit-fields-wrapper {
            display: inline-flex;
            align-items: center;
          }
          .masterresource-filter-bar input[type="date"]::-webkit-calendar-picker-indicator {
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            margin: 0;
            padding: 0;
            cursor: pointer;
            opacity: 0.55;
          }
          .masterresource-filter-bar input[type="date"]::-webkit-inner-spin-button,
          .masterresource-filter-bar input[type="date"]::-webkit-clear-button {
            display: none;
          }
        `}</style>
        <div className="masterresource-filter-bar" style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 200px", maxWidth: "200px" }}>
            <label style={filterLabelStyle}>Activity</label>
            <SelectComponent
              id="activity_filter"
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value)}
              options={activityOptions}
              showPlaceHolder="Select Activity"
              placeholderSelectable={true}
              style={{
                height: "36px", lineHeight: "34px", padding: "0 28px 0 12px", borderRadius: "8px",
                border: "1px solid #E4E7EC", fontSize: "12px", background: "#fff", color: "#1D2939",
                width: "100%", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 200px", maxWidth: "200px" }}>
            <label style={filterLabelStyle}>Category</label>
            <SelectComponent
              id="category_filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              options={categoryOptions}
              showPlaceHolder="Select Category"
              placeholderSelectable={true}
              style={{
                height: "36px", lineHeight: "34px", padding: "0 28px 0 12px", borderRadius: "8px",
                border: "1px solid #E4E7EC", fontSize: "12px", background: "#fff", color: "#1D2939",
                width: "100%", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 160px", maxWidth: "160px" }}>
            <label style={filterLabelStyle}>Unit</label>
            <SelectComponent
              id="unit_filter"
              value={unitFilter}
              onChange={(e) => setUnitFilter(e.target.value)}
              options={UNIT_FILTER_OPTIONS}
              showPlaceHolder="Select Unit"
              placeholderSelectable={true}
              style={{
                height: "36px", lineHeight: "34px", padding: "0 28px 0 12px", borderRadius: "8px",
                border: "1px solid #E4E7EC", fontSize: "12px", background: "#fff", color: "#1D2939",
                width: "100%", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 160px" }}>
            <label style={filterLabelStyle}>Created From</label>
            <input
              type="date"
              value={pendingDateFrom}
              max={pendingDateTo && pendingDateTo < TODAY_STR ? pendingDateTo : TODAY_STR}
              onChange={(e) => setPendingDateFrom(e.target.value)}
              style={{ ...filterControlStyle, width: "100%" }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 160px" }}>
            <label style={filterLabelStyle}>Created To</label>
            <input
              type="date"
              value={pendingDateTo}
              min={pendingDateFrom || undefined}
              max={TODAY_STR}
              onChange={(e) => setPendingDateTo(e.target.value)}
              style={{ ...filterControlStyle, width: "100%" }}
            />
          </div>
          <button
            type="button"
            onClick={handleApplyDateFilter}
            disabled={!pendingDateFrom || !pendingDateTo || isDateRangeInvalid}
            title="Applies the Created From / Created To date range"
            style={{
              height: "34px", padding: "0 16px", borderRadius: "6px", fontSize: "12px", fontWeight: 600,
              border: "1px solid #2E6DA4",
              background: (!pendingDateFrom || !pendingDateTo || isDateRangeInvalid) ? "#a9c3d8" : "#2E6DA4",
              color: "#fff",
              cursor: (!pendingDateFrom || !pendingDateTo || isDateRangeInvalid) ? "not-allowed" : "pointer",
            }}
          >
            Apply
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              style={{ height: "34px", padding: "0 12px", borderRadius: "8px", border: "1px solid #E4E7EC", background: "#F9FAFB", color: "#475467", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
            >
              Clear Filters
            </button>
          )}
        </div>
        {isDateRangeInvalid && (
          <div style={{ marginTop: "8px", fontSize: "11px", color: "#DC2626", fontWeight: 600 }}>
            "Created From" date must be on or before "Created To" date.
          </div>
        )}
      </div>
      <div className="datatable__container">
        {data && (
          <Table
            data={filteredData}
            addButtonLabel={t("createmasteraddbuttonlabel")}
            addNewHandler={addNewHandler}
            setSearchData={setSearchData}
            columns={columns}
            numberOfRows={10}
            searchPlaceholder="Search by Resource Name, Category, Activity, Unit"
            searchInputStyle={{ width: "19em" }}
            rowCount={filteredData.length}
            showExport={false}
            showExcelExport={true}
            exportData={filteredData}
            exportColumns={masterResourcecolumnsForExport}
            exportFileName="MasterResourceManagement"
            showFilter={false}
            showReset={false}
            onExpand={false}
            headerTableHeadStyle={{ background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)", color: "#FFFFFF" }}
            tableStyle={{
              border: "1px solid #EEF1F5",
              borderRadius: "10px",
              boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MasterResourceTable;
